import React, { useEffect, useState } from 'react'
import { postService, clientService, categoryService } from '@services/api'
import apiClient from '@services/api/client'
import toast from 'react-hot-toast'
import AdminLayout from '../../layouts/AdminLayout'
import { CATEGORIES } from '../../constants/categories'
import { useAuthStore } from '@store'

const storageURL = import.meta.env.VITE_STORAGE_URL || 'http://localhost:84/storage'
const mediaUrl = (p) => { if (!p) return null; let s = String(p); const i = s.indexOf('/storage/'); if (i !== -1) s = s.slice(i + 9); return storageURL + '/' + s.replace(/^\/+/, '') }

const mapPub = (p) => {
  const expiresAt = p.DateFinPost || p.expires_at || null
  const computedDuration = (() => {
    if (expiresAt && p.created_at) {
      const days = Math.round((new Date(expiresAt) - new Date(p.created_at)) / (1000 * 60 * 60 * 24))
      if (days > 0) return String(days)
    }
    const raw = parseInt(p.IndicePeriode || p.indiceperiode || 0)
    return String(raw > 0 ? raw : 0)
  })()
  return ({
  id: p.post_id,
  name: p.libele,
  description: p.description,
  price: p.prix ? parseFloat(p.prix) : 0,
  oldPrice: p.new_price ? parseFloat(p.new_price) : null,
  image: (() => { const f = p.fichiers?.find(f => f.is_main) ?? p.fichiers?.[0]; return f?.chemin ? mediaUrl(f.chemin) : null })(),
  images: p.fichiers?.map(f => mediaUrl(f.chemin)) || [],
  views: p.vues || 0,
  status: p.status,
  created_at: p.created_at,
  expires_at: expiresAt,
  duration: computedDuration,
  indiceperiode: p.IndicePeriode || p.indiceperiode || null,
  post_categorie_id: p.post_categorie_id,
  category: p.category?.libele_categorie || '',
  location: p.siteWeb || p.location || '',
  delivery: p.delivery || p.livraison || 'gratuite',
  condition: p.etat_appareil || p.condition || '',
  client_id: p.post_client_id,
  external_client_id: p.external_client_id || null,
  client_name: p.client ? [p.client.nom, p.client.prenom].filter(Boolean).join(' ') : '',
  client_phone: p.client?.numero || '',
  post_staff_id: p.post_staff_id,
})}


const AdminPublicationsPage = () => {
  const { user } = useAuthStore()
  const [publications, setPublications] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all') // all, active, expired
  const [filterCategory, setFilterCategory] = useState('all') // Filtre par catégorie
  const [sortBy, setSortBy] = useState('date_desc')
  const [showModal, setShowModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [selectedPublication, setSelectedPublication] = useState(null)
  const [clients, setClients] = useState([])
  const [categories, setCategories] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(6) // 6 publications par page
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    discount: '', // Remise en pourcentage
    client_id: '',
    client_type: 'existing', // 'existing' ou 'external'
    external_client_name: '',
    external_client_phone: '',
    category: '',
    location: '',
    delivery: 'gratuite',
    condition: '',
    duration: '30', // Durée en jours
    images: []
  })

  // Récupérer les rôles de l'utilisateur
  const getCurrentUserRoles = () => {
    const storedUser = localStorage.getItem('admin_user') || localStorage.getItem('user')
    if (!storedUser) return []
    try {
      const userData = JSON.parse(storedUser)
      const normalize = (r) => (typeof r === 'string' ? r : r?.name ?? '')
      if (Array.isArray(userData.roles)) return userData.roles.map(normalize)
      if (typeof userData.roles === 'string') return userData.roles.split(',').map(r => r.trim())
      if (userData.role) return [normalize(userData.role)]
    } catch (e) {}
    return []
  }

  const userRoles = getCurrentUserRoles()
  const n = (r) => r.toLowerCase()
  const isSuperAdmin = userRoles.length === 0 || userRoles.some(r => n(r) === 'super admin')
  const isAdmin = userRoles.some(r => n(r) === 'admin')
  const isSuperviseur = userRoles.some(r => n(r) === 'superviseur')
  const isAnimateur = userRoles.some(r => n(r) === 'animateur')
  const isTechnicien = userRoles.some(r => n(r) === 'technicien')
  const isClient = userRoles.some(r => n(r) === 'client')

  // Debug - à retirer après test
  console.log('🔍 Debug Permissions:', {
    userRoles,
    isSuperAdmin,
    isAdmin,
    user: user
  })

  // Récupérer l'ID du client connecté
  const getUserId = () => {
    // D'abord essayer depuis le store
    if (user?.client_id) return user.client_id
    if (user?.id) return user.id
    if (user?.user_id) return user.user_id
    
    // Sinon, essayer depuis localStorage
    try {
      const storedUser = localStorage.getItem('admin_user') || localStorage.getItem('user')
      if (storedUser) {
        const userData = JSON.parse(storedUser)
        return userData.client_id || userData.id || userData.user_id
      }
    } catch (e) {
      console.error('Erreur parsing user from localStorage:', e)
    }
    return null
  }
  
  const currentClientId = getUserId()

  console.log('📌 CurrentClientId Debug:', {
    user,
    currentClientId,
    localStorage: localStorage.getItem('admin_user') || localStorage.getItem('user')
  })

  // Fonction pour vérifier si l'utilisateur est propriétaire de la publication
  const isOwner = (pub) => {
    console.log('🔍 isOwner Check (Publication):', {
      pubId: pub.id,
      pubTitle: pub.title,
      post_staff_id: pub.post_staff_id,
      currentClientId,
      isClient,
      isAnimateur,
      comparison: pub.post_staff_id === currentClientId
    })
    
    // Pour les clients: vérifier si c'est leur propre publication
    if (isClient) {
      return pub.client_id === currentClientId || pub.client_id === String(currentClientId)
    }
    // Pour les staff (Animateur, etc.): vérifier si c'est eux qui ont créé le post
    return pub.post_staff_id === currentClientId || 
           pub.post_staff_id === String(currentClientId) ||
           Number(pub.post_staff_id) === Number(currentClientId)
  }

  // Permissions pour les boutons d'actions (fonction dynamique par publication)
  const getPermissions = (pub) => {
    console.log('🔐 getPermissions Debug (Publication):', {
      pubId: pub.id,
      pubTitle: pub.title,
      isSuperAdmin,
      isAdmin,
      isSuperviseur,
      isOwner: isOwner(pub),
      userRoles
    })
    
    // Super Admin et Admin ont TOUS les droits sur TOUTES les publications
    if (isSuperAdmin || isAdmin) {
      return {
        canView: true,
        canEdit: true,
        canArchive: true,
        canDelete: true // Vraie suppression
      }
    }

    // Superviseur : peut voir, modifier et archiver toutes les publications
    if (isSuperviseur) {
      return {
        canView: true,
        canEdit: true,
        canArchive: true,
        canDelete: false
      }
    }

    // Autres rôles : 3 boutons sur leurs propres publications (Voir, Modifier, Supprimer)
    // Le bouton "Supprimer" archive au lieu de supprimer
    if (isOwner(pub)) {
      return {
        canView: true,
        canEdit: true,
        canArchive: false, // Pas de bouton Archiver séparé
        canDelete: true    // Bouton Supprimer qui archive
      }
    }

    // Par défaut : lecture seule
    return {
      canView: true,
      canEdit: false,
      canArchive: false,
      canDelete: false
    }
  }

  useEffect(() => {
    console.log('Utilisateur connecté:', user)
    console.log('Client ID disponible:', user?.client_id || user?.id)
    loadPublications()
    loadClients()
    loadCategories()

    // Écouter l'événement d'ouverture du modal depuis le layout
    const handleOpenModal = () => setShowModal(true)
    window.addEventListener('openAddPublicationModal', handleOpenModal)

    return () => {
      window.removeEventListener('openAddPublicationModal', handleOpenModal)
    }
  }, [])

  // Réinitialiser à la page 1 quand les filtres changent
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, filterStatus, filterCategory, sortBy])

  const loadPublications = async () => {
    try {
      // Charger uniquement les publications avec post_staff_id (créées par le staff)
      // Super Admin et Admin voient TOUTES les publications staff
      // Autres utilisateurs voient uniquement leurs propres publications staff
      console.log('🔍 Load Publications Debug:', {
        isSuperAdmin,
        isAdmin,
        isClient,
        currentClientId,
        userRoles,
        user
      })

      const params = {
        type: 2,
        staff_only: 1,
        ...( (!isSuperAdmin && !isAdmin) ? { post_staff: currentClientId } : {} )
      }

      const result = await postService.getPosts(params)
      if (result) setPublications((result.data || []).map(mapPub))
    } catch (error) {
      console.error('Error loading publications:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadClients = async () => {
    try {
      const result = await clientService.getClients()
      setClients(result.data || [])
    } catch (error) {
      console.error('Error loading clients:', error)
    }
  }

  const loadCategories = async () => {
    try {
      const result = await categoryService.getCategories()
      setCategories(result.data || [])
    } catch (error) {
      console.error('Error loading categories:', error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      const prixNum = parseFloat(String(formData.price).replace(/[^0-9.]/g, '')) || 0
      const discountPct = formData.discount ? parseInt(String(formData.discount).replace(/[^0-9]/g, '')) : 0
      const computedNewPrice = (discountPct > 0 && prixNum > 0) ? Math.round(prixNum / (1 - discountPct / 100)) : null

      const formDataToSend = new FormData()
      formDataToSend.append('title', formData.title)
      formDataToSend.append('description', formData.description)
      formDataToSend.append('prix', formData.price)
      if (formData.discount) {
        formDataToSend.append('discount', formData.discount)
      }
      if (computedNewPrice) {
        formDataToSend.append('new_price', computedNewPrice)
      }
      formDataToSend.append('post_categorie_id', formData.category)
      formDataToSend.append('location', formData.location)
      formDataToSend.append('delivery', formData.delivery || 'gratuite')
      if (formData.condition) {
        formDataToSend.append('condition', formData.condition)
        formDataToSend.append('etat_appareil', formData.condition.toLowerCase()
          .replace('reconditionné', 'reconditionne')
          .replace('occasion', 'usage'))
      }
      formDataToSend.append('type', '2') // 2 = image/article
      formDataToSend.append('duration', formData.duration)
      formDataToSend.append('from_admin_panel', 'true') // Marquer comme post créé depuis le panel admin
      
      // Gérer le type de client (existant ou externe)
      if (formData.client_type === 'existing') {
        // Si un client est sélectionné, utiliser son ID, sinon utiliser l'ID de l'admin connecté
        const clientId = formData.client_id || user?.client_id || user?.id
        
        if (!clientId) {
          toast.error('Impossible de déterminer le client. Veuillez vous reconnecter.')
          return
        }
        
        formDataToSend.append('client_id', clientId)
        console.log('Client ID envoyé:', clientId)
      } else {
        // Client externe (sans compte)
        formDataToSend.append('external_client', '1')
        formDataToSend.append('external_client_name', formData.external_client_name)
        formDataToSend.append('external_client_phone', formData.external_client_phone)
      }

      // Ajouter l'ID du créateur staff (l'utilisateur connecté qui crée le post)
      const staffCreatorId = user?.client_id || user?.id
      if (staffCreatorId) {
        formDataToSend.append('staff_creator_id', staffCreatorId)
        console.log('Staff Creator ID envoyé:', staffCreatorId)
      }
      
      // Ajouter les images
      formData.images.forEach((image) => {
        formDataToSend.append('files[]', image)
      })

      const result = await postService.createPost(formDataToSend)

      if (result) {
        toast.success('✨ Publication créée avec succès!')
        setShowModal(false)
        loadPublications()
        // Réinitialiser le formulaire
        setFormData({
          title: '',
          description: '',
          price: '',
          discount: '',
          client_id: '',
          client_type: 'existing',
          external_client_name: '',
          external_client_phone: '',
          duration: '30',
          category: '',
          location: '',
          delivery: 'gratuite',
          condition: '',
          images: []
        })
      }
    } catch (error) {
      console.error('Error creating publication:', error)
      toast.error(error.response?.data?.message || 'Erreur lors de la création de la publication')
    }
  }

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files)
    const currentImages = formData.images
    const totalImages = currentImages.length + files.length
    
    if (totalImages > 4) {
      toast.error(`⚠️ Vous ne pouvez ajouter que ${4 - currentImages.length} image(s) supplémentaire(s). Maximum 4 images au total.`)
      e.target.value = '' // Réinitialiser l'input
      return
    }
    
    // Ajouter les nouvelles images aux images existantes
    setFormData({ ...formData, images: [...currentImages, ...files] })
    e.target.value = '' // Réinitialiser l'input pour permettre de sélectionner à nouveau
  }

  const handleDelete = async () => {
    if (!selectedPublication) return

    try {
      // Si l'utilisateur est Super Admin ou Admin, vraie suppression
      // Sinon, archivage (status = 2) - mais l'utilisateur croit que c'est une suppression
      if (isSuperAdmin || isAdmin) {
        await postService.deletePost(selectedPublication.id)
        toast.success('🗑️ Publication supprimée avec succès')
        setShowDeleteModal(false)
        setSelectedPublication(null)
        loadPublications()
      } else {
        // Pour les autres utilisateurs : archivage au lieu de suppression (invisible pour eux)
        await postService.archivePost(selectedPublication.id)
        toast.success('🗑️ Publication supprimée avec succès')
        setShowDeleteModal(false)
        setSelectedPublication(null)

        // Mettre à jour immédiatement le state local
        setPublications(prevPubs =>
          prevPubs.map(p =>
            p.id === selectedPublication.id
              ? { ...p, status: 3 }
              : p
          )
        )

        // Recharger après un court délai
        setTimeout(() => {
          loadPublications()
        }, 300)
      }
    } catch (error) {
      console.error('Error deleting/archiving publication:', error)
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression')
    }
  }

  const handleToggleArchive = async () => {
    if (!selectedPublication) return
    
    const isArchiving = selectedPublication.status !== 3
    const action = isArchiving ? 'archiver' : 'désarchiver'

    try {
      await postService.archivePost(selectedPublication.id)
      toast.success(`📦 Publication ${isArchiving ? 'archivée' : 'désarchivée'} avec succès`)
      setShowArchiveModal(false)
      setSelectedPublication(null)
      loadPublications()
    } catch (error) {
      console.error('Error archiving publication:', error)
      toast.error(error.response?.data?.message || `Erreur lors de l'${action} de la publication`)
    }
  }

  const openViewModal = (pub) => {
    setSelectedPublication(pub)
    setShowViewModal(true)
  }

  const openEditModal = (pub) => {
    setSelectedPublication(pub)
    
    // Calculer la remise depuis new_price (ancien prix) et prix actuel
    let calculatedDiscount = ''
    if (pub.oldPrice && pub.price && pub.oldPrice > pub.price) {
      const discountPercent = Math.round((1 - pub.price / pub.oldPrice) * 100)
      if (discountPercent > 0) {
        calculatedDiscount = `${discountPercent}%`
      }
    }
    
    // Calculer la durée restante en jours
    let remainingDays = 30
    if (pub.expires_at) {
      const now = new Date()
      const expiryDate = new Date(pub.expires_at)
      const diffTime = expiryDate - now
      remainingDays = Math.max(1, Math.floor(diffTime / (1000 * 60 * 60 * 24)))
    }
    
    const isExternal = !!pub.external_client_id

    setFormData({
      title: pub.name || '',
      description: pub.description || '',
      price: pub.price || '',
      discount: calculatedDiscount,
      client_id: isExternal ? '' : (pub.client_id || ''),
      client_type: isExternal ? 'external' : 'existing',
      external_client_name: isExternal ? pub.client_name : '',
      external_client_phone: isExternal ? pub.client_phone : '',
      category: pub.post_categorie_id || '',
      location: pub.location || '',
      delivery: pub.delivery || 'gratuite',
      condition: (() => {
        const c = pub.condition || ''
        if (!c) return ''
        const map = { neuf: 'Neuf', occasion: 'Occasion', usage: 'Occasion', reconditionne: 'Reconditionné', reconditionné: 'Reconditionné' }
        return map[c.toLowerCase()] || c
      })(),
      duration: remainingDays.toString(),
      images: pub.images || []
    })
    setShowEditModal(true)
  }

  const openDeleteModal = (pub) => {
    setSelectedPublication(pub)
    setShowDeleteModal(true)
  }

  const openArchiveModal = (pub) => {
    setSelectedPublication(pub)
    setShowArchiveModal(true)
  }

  const filteredPublications = publications.filter(pub => {
    const matchesSearch = pub.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pub.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pub.description?.toLowerCase().includes(searchTerm.toLowerCase())
    
    let matchesStatus = true
    if (filterStatus === 'active') {
      const isExpired = pub.expires_at ? new Date(pub.expires_at) < new Date() : false
      matchesStatus = pub.status === 2 && !isExpired
    } else if (filterStatus === 'expired') {
      const isExpired = pub.expires_at ? new Date(pub.expires_at) < new Date() : false
      matchesStatus = pub.status !== 3 && isExpired
    } else if (filterStatus === 'archived') {
      matchesStatus = pub.status === 3
    }
    // Si filterStatus === 'all', on affiche tout (actives, expirées ET archivées)
    
    const matchesCategory = filterCategory === 'all' || pub.category === filterCategory
    
    return matchesSearch && matchesStatus && matchesCategory
  })
  
  // Appliquer le tri
  const sortedPublications = [...filteredPublications].sort((a, b) => {
    switch (sortBy) {
      case 'date_desc':
        return new Date(b.created_at || 0) - new Date(a.created_at || 0)
      case 'date_asc':
        return new Date(a.created_at || 0) - new Date(b.created_at || 0)
      case 'name_asc':
        return (a.name || '').localeCompare(b.name || '')
      case 'name_desc':
        return (b.name || '').localeCompare(a.name || '')
      case 'views_desc':
        return (b.views || 0) - (a.views || 0)
      case 'views_asc':
        return (a.views || 0) - (b.views || 0)
      case 'price_desc':
        return (b.price || 0) - (a.price || 0)
      case 'price_asc':
        return (a.price || 0) - (b.price || 0)
      default:
        return 0
    }
  })

  // Pagination
  const totalPages = Math.ceil(sortedPublications.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentPublications = sortedPublications.slice(startIndex, endIndex)

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-400 text-lg">Chargement...</div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="space-y-6 mt-10">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-gray-800">Publications</h1>
              <p className="text-gray-500 mt-2 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Gérer toutes les publications articles
              </p>
            </div>
            <button 
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#c09d2a] text-white rounded-lg hover:shadow-lg transition-all font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Nouvelle publication
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Total */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Total</p>
                <p className="text-2xl font-bold text-gray-800">{publications.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Actives */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Actives</p>
                <p className="text-2xl font-bold text-green-600">
                  {publications.filter(p => p.status === 2).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Expirées */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Expirées</p>
                <p className="text-2xl font-bold text-red-600">
                  {publications.filter(p => {
                    if (p.status === 3) return false
                    const isExpired = p.expires_at ? new Date(p.expires_at) < new Date() : false
                    return isExpired
                  }).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Archivées */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Archivées</p>
                <p className="text-2xl font-bold text-orange-600">
                  {publications.filter(p => p.status === 3).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="relative md:col-span-2 lg:col-span-1">
              <svg className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Rechercher par titre ou client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#001F3F] focus:border-transparent transition-all"
              />
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#001F3F] focus:border-transparent bg-white transition-all"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">✓ Actives</option>
              <option value="expired">⏰ Expirées</option>
              <option value="archived">📦 Archivées</option>
            </select>

            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#001F3F] focus:border-transparent bg-white transition-all"
            >
              <option value="all">Toutes les catégories</option>
              {CATEGORIES.filter(cat => cat !== 'Toutes les catégories').map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#001F3F] focus:border-transparent bg-white transition-all"
            >
              <option value="date_desc">📅 Plus récents</option>
              <option value="date_asc">📅 Plus anciens</option>
              <option value="name_asc">🔤 Titre A-Z</option>
              <option value="name_desc">🔤 Titre Z-A</option>
              <option value="views_desc">👁️ Plus de vues</option>
              <option value="views_asc">👁️ Moins de vues</option>
              <option value="price_desc">💰 Prix décroissant</option>
              <option value="price_asc">💰 Prix croissant</option>
            </select>
          </div>
        </div>

        {/* Table des publications */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">Liste des publications</h2>
              <span className="text-sm text-gray-500">{sortedPublications.length} publication{sortedPublications.length > 1 ? 's' : ''}</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Titre</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Date d'ajout</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Nb jours</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Jours restants</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Date fin</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Vues</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {currentPublications.map((pub, index) => {
                  const isActive = pub.status === 2   // 2 = publié/en cours
                  const isArchived = pub.status === 3  // 3 = archivé/terminé
                  const isPending = pub.status === 1   // 1 = en attente
                  const categoryName = pub.category || 'Sans catégorie'
                  return (
                    <tr key={pub.id} className={`hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 ${
                      isArchived ? 'bg-gray-100 opacity-60' : ''
                    }`}>
                      <td className="px-6 py-4">
                        {pub.image ? (
                          <img 
                            src={pub.image} 
                            alt={pub.name}
                            className="w-16 h-16 object-cover rounded-lg shadow-sm"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-800">
                          {pub.name && pub.name.split(' ').slice(0, 2).join(' ')}
                          {pub.name && pub.name.split(' ').length > 2 && '...'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {pub.description && pub.description.split(' ').slice(0, 2).join(' ')}
                          {pub.description && pub.description.split(' ').length > 2 && '...'}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-700 font-medium">
                          {pub.client_name || 'N/A'}
                        </p>
                        {pub.client_phone && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {pub.client_phone}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {isArchived ? (
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium flex items-center gap-1 w-fit">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                              <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
                            </svg>
                            Archivée
                          </span>
                        ) : isPending ? (
                          <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium flex items-center gap-1 w-fit">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            En attente
                          </span>
                        ) : isActive ? (
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1 w-fit">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Active
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium flex items-center gap-1 w-fit">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            Expirée
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">
                          {pub.created_at ? new Date(pub.created_at).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          }) : 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                          {(() => {
                            const d = parseInt(pub.duration)
                            return d > 0 ? `${d} ${d > 1 ? 'jours' : 'jour'}` : 'N/A'
                          })()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {pub.expires_at ? (() => {
                          const now = new Date();
                          const expiryDate = new Date(pub.expires_at);
                          const diffTime = expiryDate - now;
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                          
                          if (diffDays < 0) {
                            return <span className="px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold inline-flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                              Expiré
                            </span>;
                          } else if (diffDays === 0) {
                            return <span className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold inline-flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                              </svg>
                              Aujourd'hui
                            </span>;
                          } else if (diffDays <= 3) {
                            return <span className="px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold inline-flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                              </svg>
                              {diffDays} {diffDays === 1 ? 'jour' : 'jours'}
                            </span>;
                          } else if (diffDays <= 7) {
                            return <span className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold inline-flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                              </svg>
                              {diffDays} jours
                            </span>;
                          } else {
                            return <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold inline-flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              {diffDays} jours
                            </span>;
                          }
                        })() : <span className="text-sm text-gray-400">N/A</span>}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">
                          {pub.expires_at ? new Date(pub.expires_at).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          }) : 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        <span className="flex items-center gap-1.5">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          {pub.views || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {getPermissions(pub).canView && (
                            <button 
                              onClick={() => openViewModal(pub)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Voir"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                          )}
                          {getPermissions(pub).canEdit && (
                            <button 
                              onClick={() => openEditModal(pub)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Modifier"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          {getPermissions(pub).canArchive && (
                            <button 
                              onClick={() => openArchiveModal(pub)}
                              className={`p-2 ${isArchived ? 'text-green-600 hover:bg-green-50' : 'text-orange-600 hover:bg-orange-50'} rounded-lg transition-colors`}
                              title={isArchived ? 'Désarchiver' : 'Archiver'}
                            >
                              {isArchived ? (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                </svg>
                              )}
                            </button>
                          )}
                          {getPermissions(pub).canDelete && (
                            <button 
                              onClick={() => openDeleteModal(pub)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Supprimer"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {sortedPublications.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <div className="text-6xl mb-4">📄</div>
                <p className="text-lg">Aucune publication trouvée</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {sortedPublications.length > 0 && totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Affichage de {startIndex + 1} à {Math.min(endIndex, sortedPublications.length)} sur {sortedPublications.length} publications
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                          currentPage === page
                            ? 'bg-[#D4AF37] text-white font-semibold'
                            : 'bg-white border border-gray-300 hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de création */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col animate-scaleIn">
            {/* Header - Fixed */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Créer une publication sponsorisée</h2>
              </div>
              <button 
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body - Scrollable */}

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
              
              {/* Macaron Publication Staff */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-pink-600 font-semibold text-sm">📢 Publication Staff</span>
                  </div>
                  <p className="text-blue-700 text-sm">
                    Cette publication sera publiée en tant que (Staff) et s'affichera dans la section publicité du dashboard public.
                  </p>
                </div>
              </div>

              {/* Type de Client */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de client <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="client_type"
                      value="existing"
                      checked={formData.client_type === 'existing'}
                      onChange={(e) => setFormData({ ...formData, client_type: e.target.value })}
                      className="w-4 h-4 text-[#D4AF37] focus:ring-[#D4AF37]"
                    />
                    <span className="text-sm text-gray-700">Client existant</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="client_type"
                      value="external"
                      checked={formData.client_type === 'external'}
                      onChange={(e) => setFormData({ ...formData, client_type: e.target.value })}
                      className="w-4 h-4 text-[#D4AF37] focus:ring-[#D4AF37]"
                    />
                    <span className="text-sm text-gray-700">Client externe (sans compte)</span>
                  </label>
                </div>
              </div>

              {/* Champ Client Existant */}
              {formData.client_type === 'existing' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sélectionner le client
                  </label>
                  <select
                    value={formData.client_id}
                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  >
                    <option value="">Moi-même (Admin connecté)</option>
                    {clients.map((client) => (
                      <option key={client.client_id} value={client.client_id}>
                        {client.nom} {client.prenom} - {client.telephone}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Champs Client Externe */}
              {formData.client_type === 'external' && (
                <div className="space-y-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Pour un client qui n'a pas de compte sur la plateforme
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom complet du client <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.external_client_name}
                      onChange={(e) => setFormData({ ...formData, external_client_name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                      placeholder="Ex: Jean Kouassi"
                      required={formData.client_type === 'external'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Numéro de téléphone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.external_client_phone}
                      onChange={(e) => setFormData({ ...formData, external_client_phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                      placeholder="Ex: 0707654321"
                      required={formData.client_type === 'external'}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Ces informations apparaîtront dans la page détails de l'article
                    </p>
                  </div>
                </div>
              )}

              {/* Titre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  placeholder="Ex: iPhone 15 Pro Max"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  placeholder="Description détaillée du produit..."
                  required
                />
              </div>

              {/* Prix et Remise */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prix (FCFA) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                    placeholder="Ex: 500000"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Remise (%)
                  </label>
                  <select
                    value={formData.discount}
                    onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent bg-white"
                  >
                    <option value="">Aucune remise</option>
                    <option value="5%">5%</option>
                    <option value="7%">7%</option>
                    <option value="10%">10%</option>
                    <option value="15%">15%</option>
                    <option value="20%">20%</option>
                    <option value="25%">25%</option>
                    <option value="30%">30%</option>
                    <option value="35%">35%</option>
                    <option value="40%">40%</option>
                    <option value="50%">50%</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Le prix réduit sera calculé automatiquement
                  </p>
                </div>
              </div>

              {/* Catégorie */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catégorie <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent bg-white"
                  required
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map((cat) => (
                    <option key={cat.categorie_id} value={cat.categorie_id}>
                      {cat.libele_categorie}
                    </option>
                  ))}
                </select>
              </div>

              {/* Localisation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Localisation <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  placeholder="Ex: Abidjan, Cocody"
                  required
                />
              </div>

              {/* Livraison et État */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Livraison
                  </label>
                  <select
                    value={formData.delivery}
                    onChange={(e) => setFormData({ ...formData, delivery: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent bg-white"
                  >
                    <option value="gratuite">Gratuite</option>
                    <option value="payante">Payante</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    État
                  </label>
                  <select
                    value={formData.condition}
                    onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent bg-white"
                  >
                    <option value="">Sélectionner</option>
                    <option value="Neuf">Neuf</option>
                    <option value="Occasion">Occasion</option>
                    <option value="Reconditionné">Reconditionné</option>
                  </select>
                </div>
              </div>

              {/* Durée d'affichage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Durée d'affichage (jours) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  placeholder="Ex: 30"
                  min="1"
                  max="365"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Nombre de jours pendant lesquels la publication sera visible (1-365 jours)
                </p>
              </div>

              {/* Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Images <span className="text-red-500">*</span>
                </label>
                
                {/* Input file caché */}
                <input
                  ref={(input) => {
                    if (input && !window.imageFileInput) {
                      window.imageFileInput = input
                    }
                  }}
                  type="file"
                  onChange={handleImageChange}
                  accept="image/*"
                  multiple
                  className="hidden"
                  id="image-upload"
                />

                <div className="flex items-start gap-2 mb-3">
                  {formData.images.length === 4 ? (
                    <svg className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  ) : null}
                  <p className={`text-xs ${
                    formData.images.length === 4 ? 'text-orange-600 font-medium' : 'text-gray-500'
                  }`}>
                    {formData.images.length > 0 
                      ? `${formData.images.length}/4 image(s) sélectionnée(s)${formData.images.length === 4 ? ' (Maximum atteint)' : ''}` 
                      : 'Cliquez sur les cartes pour ajouter des images (maximum 4)'}
                  </p>
                </div>

                {/* Aperçu des images sélectionnées */}
                <div className="mt-4 grid grid-cols-4 gap-3">
                  {/* Images sélectionnées */}
                  {formData.images.map((image, index) => (
                    <div key={index} className="relative group">
                      <div className="aspect-square rounded-lg overflow-hidden border-2 border-dashed border-[#D4AF37] bg-white shadow-sm">
                        <img
                          src={URL.createObjectURL(image)}
                          alt={`Aperçu ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      {/* Badge numéro */}
                      <div className="absolute top-2 left-2 w-8 h-8 bg-[#D4AF37] rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-white text-sm font-bold">{index + 1}</span>
                      </div>
                      {/* Bouton supprimer */}
                      <button
                        type="button"
                        onClick={() => {
                          const newImages = formData.images.filter((_, i) => i !== index)
                          setFormData({ ...formData, images: newImages })
                        }}
                        className="absolute top-2 right-2 w-7 h-7 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  
                  {/* Cases vides pour les images restantes */}
                  {[...Array(4 - formData.images.length)].map((_, index) => (
                    <div
                      key={`empty-${index}`}
                      onClick={() => document.getElementById('image-upload').click()}
                      className="aspect-square rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center cursor-pointer hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all group"
                    >
                      <div className="w-10 h-10 rounded-full bg-gray-400 group-hover:bg-[#D4AF37] flex items-center justify-center transition-colors">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              </div>

              {/* Footer - Fixed */}
              <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#c09d2a] text-white rounded-lg hover:shadow-lg transition-all font-medium"
                >
                  Créer la publication
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Voir les détails */}
      {showViewModal && selectedPublication && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 animate-fadeIn backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col animate-scaleIn">
            {/* Header */}
            <div className="px-8 py-6 bg-blue-50/50 border-b-2 border-blue-100 rounded-t-2xl flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-blue-600">Détails de la publication</h2>
                  <p className="text-blue-500 text-sm mt-0.5">Informations complètes du produit</p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedPublication(null)
                }}
                className="p-2 hover:bg-blue-50 rounded-xl transition-all duration-200"
              >
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-8 space-y-8 overflow-y-auto flex-1 bg-gray-50">
              {/* Images */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <h3 className="text-lg font-bold text-gray-800">Images du produit</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {selectedPublication.images && selectedPublication.images.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img 
                        src={img} 
                        alt={`Image ${idx + 1}`}
                        className="w-full h-40 object-cover rounded-xl border-2 border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
                      />
                      <div className="absolute top-2 left-2 w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                        <span className="text-white text-sm font-bold">{idx + 1}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Informations principales */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-6">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-bold text-gray-800">Informations générales</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-lg p-4 border border-blue-200">
                    <label className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Titre</label>
                    <p className="text-base font-bold text-gray-900 mt-2">{selectedPublication.name}</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-lg p-4 border border-purple-200">
                    <label className="text-xs font-semibold text-purple-600 uppercase tracking-wide">Catégorie</label>
                    <p className="mt-2">
                      <span className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold shadow-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        {selectedPublication.category}
                      </span>
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-lg p-4 border border-green-200">
                    <label className="text-xs font-semibold text-green-600 uppercase tracking-wide">Prix</label>
                    <div className="mt-2">
                      <p className="text-2xl font-bold text-green-700">
                        {selectedPublication.price?.toLocaleString('fr-FR')} <span className="text-sm">FCFA</span>
                      </p>
                      {selectedPublication.oldPrice && (
                        <p className="text-sm text-gray-500 line-through mt-1">
                          {selectedPublication.oldPrice?.toLocaleString('fr-FR')} FCFA
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-lg p-4 border border-orange-200">
                    <label className="text-xs font-semibold text-orange-600 uppercase tracking-wide">Client</label>
                    <p className="text-base font-semibold text-gray-900 mt-2">{selectedPublication.client_name}</p>
                    <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      {selectedPublication.client_phone}
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-red-50 to-red-100/50 rounded-lg p-4 border border-red-200">
                    <label className="text-xs font-semibold text-red-600 uppercase tracking-wide">Localisation</label>
                    <p className="text-base font-semibold text-gray-900 mt-2 flex items-center gap-2">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {selectedPublication.location}
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-indigo-50 to-indigo-100/50 rounded-lg p-4 border border-indigo-200">
                    <label className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Livraison & État</label>
                    <div className="mt-2 flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${
                        selectedPublication.delivery === 'gratuite' 
                          ? 'bg-green-600 text-white' 
                          : 'bg-orange-600 text-white'
                      }`}>
                        {selectedPublication.delivery === 'gratuite' ? '🎁 Gratuite' : '💰 Payante'}
                      </span>
                      <span className="px-3 py-1 bg-indigo-600 text-white rounded-lg text-sm font-semibold">
                        {selectedPublication.condition || 'N/A'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-cyan-50 to-cyan-100/50 rounded-lg p-4 border border-cyan-200">
                    <label className="text-xs font-semibold text-cyan-600 uppercase tracking-wide">Statistiques</label>
                    <div className="mt-2 flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <span className="text-base font-bold text-gray-900">{selectedPublication.views}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-gray-700">
                          {selectedPublication.created_at ? new Date(selectedPublication.created_at).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          }) : selectedPublication.date}
                        </span>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-cyan-200">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Début:</span>
                        <span className="font-semibold text-gray-900">
                          {selectedPublication.created_at ? new Date(selectedPublication.created_at).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          }) : 'N/A'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-2">
                        <span className="text-gray-600">Fin:</span>
                        <span className="font-semibold text-gray-900">
                          {selectedPublication.expires_at ? new Date(selectedPublication.expires_at).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          }) : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 rounded-lg p-4 border border-teal-200">
                    <label className="text-xs font-semibold text-teal-600 uppercase tracking-wide">Temps restant</label>
                    <div className="mt-2">
                      {selectedPublication.expires_at ? (() => {
                        const now = new Date();
                        const expiryDate = new Date(selectedPublication.expires_at);
                        const diffTime = expiryDate - now;
                        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                        
                        if (diffDays < 0) {
                          return <span className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-bold inline-flex items-center gap-2">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            Expiré
                          </span>;
                        } else if (diffDays === 0) {
                          return <span className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg text-sm font-bold inline-flex items-center gap-2">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            Expire aujourd'hui
                          </span>;
                        } else if (diffDays === 1) {
                          return <span className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg text-sm font-bold inline-flex items-center gap-2">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            1 jour restant
                          </span>;
                        } else if (diffDays <= 7) {
                          return <span className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg text-sm font-bold inline-flex items-center gap-2">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            {diffDays} jours restants
                          </span>;
                        } else {
                          return <span className="px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-bold inline-flex items-center gap-2">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            {diffDays} jours restants
                          </span>;
                        }
                      })() : <span className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium">Non défini</span>}
                    </div>
                    <div className="mt-3 pt-3 border-t border-teal-200">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Durée d'affichage:</span>
                        <span className="font-semibold text-gray-900">
                          {(() => {
                            const d = parseInt(selectedPublication.duration)
                            return d > 0 ? `${d} ${d > 1 ? 'jours' : 'jour'}` : 'N/A'
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                  </svg>
                  <h3 className="text-lg font-bold text-gray-800">Description détaillée</h3>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-base text-gray-700 leading-relaxed whitespace-pre-line">
                    {selectedPublication.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-8 py-4 border-t border-gray-200 bg-white rounded-b-2xl flex justify-end">
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedPublication(null)
                }}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:shadow-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifier */}
      {showEditModal && selectedPublication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col animate-scaleIn">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Modifier la publication</h2>
              </div>
              <button 
                onClick={() => {
                  setShowEditModal(false)
                  setSelectedPublication(null)
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault()
              try {
                const newImages = formData.images.filter(img => typeof img !== 'string')

                const updPrixNum = parseFloat(String(formData.price).replace(/[^0-9.]/g, '')) || 0
                const updDiscountPct = formData.discount ? parseInt(String(formData.discount).replace(/[^0-9]/g, '')) : 0
                const updNewPrice = (updDiscountPct > 0 && updPrixNum > 0) ? Math.round(updPrixNum / (1 - updDiscountPct / 100)) : null

                const appendClient = (fd) => {
                  if (formData.client_type === 'existing') {
                    fd.append('client_id', formData.client_id || '')
                    fd.append('post_client_id', formData.client_id || '')
                  } else {
                    fd.append('external_client', '1')
                    fd.append('external_client_name', formData.external_client_name || '')
                    fd.append('external_client_phone', formData.external_client_phone || '')
                  }
                }

                if (newImages.length > 0) {
                  // Method spoofing — PHP ne parse pas multipart/form-data pour PUT
                  const fd = new FormData()
                  fd.append('_method', 'PUT')
                  fd.append('title', formData.title)
                  fd.append('description', formData.description)
                  fd.append('prix', formData.price)
                  if (formData.discount) fd.append('discount', formData.discount)
                  if (updNewPrice) fd.append('new_price', updNewPrice)
                  fd.append('post_categorie_id', formData.category)
                  fd.append('location', formData.location)
                  fd.append('delivery', formData.delivery || 'gratuite')
                  if (formData.condition) {
                    fd.append('condition', formData.condition)
                    fd.append('etat_appareil', formData.condition.toLowerCase()
                      .replace('reconditionné', 'reconditionne')
                      .replace('occasion', 'usage'))
                  }
                  if (formData.duration) fd.append('duration', formData.duration)
                  appendClient(fd)
                  newImages.forEach(file => fd.append('files[]', file))
                  await apiClient.post(`/posts/${selectedPublication.id}`, fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                  })
                } else {
                  const clientPayload = formData.client_type === 'existing'
                    ? { client_id: formData.client_id || '', post_client_id: formData.client_id || '' }
                    : { external_client: 1, external_client_name: formData.external_client_name, external_client_phone: formData.external_client_phone }

                  await postService.updatePost(selectedPublication.id, {
                    title: formData.title,
                    description: formData.description,
                    prix: formData.price,
                    discount: formData.discount || undefined,
                    new_price: updNewPrice || undefined,
                    post_categorie_id: formData.category,
                    location: formData.location,
                    delivery: formData.delivery || 'gratuite',
                    condition: formData.condition || undefined,
                    etat_appareil: formData.condition ? formData.condition.toLowerCase()
                      .replace('reconditionné', 'reconditionne')
                      .replace('occasion', 'usage') : undefined,
                    duration: formData.duration || undefined,
                    ...clientPayload,
                  })
                }
                toast.success('✅ Publication modifiée avec succès!')
                setShowEditModal(false)
                setSelectedPublication(null)
                loadPublications()
              } catch (error) {
                console.error('Error updating publication:', error)
                toast.error(error.response?.data?.message || 'Erreur lors de la modification: ' + error.message)
              }
            }} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 space-y-4 overflow-y-auto flex-1">

                {/* Type de client */}
                <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Type de client <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="existing"
                        checked={formData.client_type === 'existing'}
                        onChange={(e) => setFormData({ ...formData, client_type: e.target.value })}
                        className="w-4 h-4 text-[#D4AF37] focus:ring-[#D4AF37]"
                      />
                      <span className="ml-2 text-gray-700">Client existant</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="external"
                        checked={formData.client_type === 'external'}
                        onChange={(e) => setFormData({ ...formData, client_type: e.target.value })}
                        className="w-4 h-4 text-[#D4AF37] focus:ring-[#D4AF37]"
                      />
                      <span className="ml-2 text-gray-700">Client externe (sans compte)</span>
                    </label>
                  </div>
                </div>

                {/* Sélection du client existant */}
                {formData.client_type === 'existing' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sélectionner le client <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.client_id}
                      onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    >
                      <option value="">-- Choisir un client --</option>
                      {clients.map(client => (
                        <option key={client.client_id} value={client.client_id}>
                          {client.nom} {client.prenom} - {client.email}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Informations client externe */}
                {formData.client_type === 'external' && (
                  <div className="space-y-4 border-2 border-orange-200 rounded-lg p-4 bg-orange-50">
                    <div className="flex items-center gap-2 text-orange-700 mb-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="font-semibold">Informations du client externe</span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nom complet <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.external_client_name}
                        onChange={(e) => setFormData({ ...formData, external_client_name: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Ex: Jean Kouassi"
                        required={formData.client_type === 'external'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Numéro de téléphone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={formData.external_client_phone}
                        onChange={(e) => setFormData({ ...formData, external_client_phone: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Ex: +225 07 XX XX XX XX"
                        required={formData.client_type === 'external'}
                      />
                    </div>
                  </div>
                )}

                {/* Titre */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Ex: iPhone 15 Pro Max"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Description détaillée du produit..."
                    required
                  />
                </div>

                {/* Prix et Remise */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prix (FCFA) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Ex: 500000"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Remise (%)
                    </label>
                    <select
                      value={formData.discount}
                      onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                    >
                      <option value="">Aucune remise</option>
                      <option value="5%">5%</option>
                      <option value="7%">7%</option>
                      <option value="10%">10%</option>
                      <option value="15%">15%</option>
                      <option value="20%">20%</option>
                      <option value="25%">25%</option>
                      <option value="30%">30%</option>
                      <option value="35%">35%</option>
                      <option value="40%">40%</option>
                      <option value="50%">50%</option>
                    </select>
                  </div>
                </div>

                {/* Catégorie */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Catégorie <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                    required
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map((cat) => (
                      <option key={cat.categorie_id} value={cat.categorie_id}>
                        {cat.libele_categorie}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Localisation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Localisation <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Ex: Abidjan, Cocody"
                    required
                  />
                </div>

                {/* Livraison et État */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Livraison
                    </label>
                    <select
                      value={formData.delivery}
                      onChange={(e) => setFormData({ ...formData, delivery: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                    >
                      <option value="gratuite">Gratuite</option>
                      <option value="payante">Payante</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      État
                    </label>
                    <select
                      value={formData.condition}
                      onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                    >
                      <option value="">Sélectionner</option>
                      <option value="Neuf">Neuf</option>
                      <option value="Occasion">Occasion</option>
                      <option value="Reconditionné">Reconditionné</option>
                    </select>
                  </div>
                </div>

                {/* Durée d'affichage */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Durée d'affichage (jours) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Ex: 30"
                    min="1"
                    max="365"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Nombre de jours pendant lesquels la publication sera visible (1-365 jours)
                  </p>
                </div>

                {/* Images actuelles */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Images actuelles
                  </label>
                  <div className="grid grid-cols-4 gap-3 mb-3">
                    {formData.images.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img 
                          src={typeof img === 'string' ? img : URL.createObjectURL(img)} 
                          alt={`Image ${idx + 1}`}
                          className="w-full h-24 object-cover rounded-lg border-2 border-green-300"
                        />
                        <div className="absolute top-2 left-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">{idx + 1}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newImages = formData.images.filter((_, i) => i !== idx)
                            setFormData({ ...formData, images: newImages })
                          }}
                          className="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                    
                    {/* Cases vides pour ajouter des images */}
                    {formData.images.length < 4 && [...Array(4 - formData.images.length)].map((_, index) => (
                      <div
                        key={`empty-${index}`}
                        onClick={() => document.getElementById('edit-image-upload').click()}
                        className="aspect-square h-24 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center cursor-pointer hover:border-green-500 hover:bg-green-50 transition-all group"
                      >
                        <div className="w-8 h-8 rounded-full bg-gray-400 group-hover:bg-green-500 flex items-center justify-center transition-colors">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Input file caché pour ajouter des images */}
                  <input
                    id="edit-image-upload"
                    type="file"
                    onChange={(e) => {
                      const files = Array.from(e.target.files)
                      const currentImages = formData.images
                      const totalImages = currentImages.length + files.length
                      
                      if (totalImages > 4) {
                        toast.error(`⚠️ Vous ne pouvez ajouter que ${4 - currentImages.length} image(s) supplémentaire(s). Maximum 4 images au total.`)
                        e.target.value = ''
                        return
                      }
                      
                      setFormData({ ...formData, images: [...currentImages, ...files] })
                      e.target.value = ''
                    }}
                    accept="image/*"
                    multiple
                    className="hidden"
                  />
                  
                  <p className="text-xs text-gray-500">
                    {formData.images.length}/4 image(s) - Cliquez sur les cases vides pour ajouter des images
                  </p>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex gap-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setSelectedPublication(null)
                  }}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:shadow-lg transition-all font-medium"
                >
                  Enregistrer les modifications
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Confirmation Suppression */}
      {showDeleteModal && selectedPublication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-scaleIn">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Supprimer la publication</h3>
                  <p className="text-sm text-gray-500 mt-1">Cette action est irréversible</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Êtes-vous sûr de vouloir supprimer cette publication ?
              </p>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="font-semibold text-gray-800">{selectedPublication.name}</p>
                <p className="text-sm text-gray-600 mt-1">{selectedPublication.category}</p>
              </div>
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-800">
                  Attention : Cette publication sera définitivement supprimée de la base de données.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setSelectedPublication(null)
                }}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:shadow-lg transition-all font-medium"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation Archivage */}
      {showArchiveModal && selectedPublication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-scaleIn">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 ${selectedPublication.status === 2 ? 'bg-green-100' : 'bg-orange-100'} rounded-full flex items-center justify-center`}>
                  {selectedPublication.status === 2 ? (
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">
                    {selectedPublication.status === 2 ? 'Désarchiver' : 'Archiver'} la publication
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedPublication.status === 2 ? 'Rendre visible à nouveau' : 'Masquer temporairement'}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-4">
                {selectedPublication.status === 2 
                  ? 'Cette publication sera à nouveau visible sur la plateforme.'
                  : 'Cette publication sera masquée mais pourra être restaurée plus tard.'}
              </p>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="font-semibold text-gray-800">{selectedPublication.name}</p>
                <p className="text-sm text-gray-600 mt-1">{selectedPublication.category}</p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex gap-3">
              <button
                onClick={() => {
                  setShowArchiveModal(false)
                  setSelectedPublication(null)
                }}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleToggleArchive}
                className={`flex-1 px-6 py-3 bg-gradient-to-r ${selectedPublication.status === 2 ? 'from-green-500 to-green-600' : 'from-orange-500 to-orange-600'} text-white rounded-lg hover:shadow-lg transition-all font-medium`}
              >
                {selectedPublication.status === 2 ? 'Oui, désarchiver' : 'Oui, archiver'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

export default AdminPublicationsPage
